/**
 * Reading the host object, which nobody vouches for.
 *
 * The model context arrives here as a parameter and never through `document`.
 * That is the whole shape of this package: `apps/jig/src/platform` is the single
 * feature detection point, enforced by the `jig/model-context-gate` lint rule, and
 * requirement 20.3 with Property S2 only hold while it stays the only one. A
 * package reaching for `document.modelContext` would be a second detection point
 * outside the rule's scope. Injection also means every branch below is testable
 * against a plain object, which is what {@link createFakeHost} is for.
 *
 * Every read goes through a guard rather than a bare property access, because the
 * value came from a draft browser API and can be a half built object or a getter
 * that raises.
 */

/** Whether a value can carry properties at all, functions included. */
export const isObjectLike = (value: unknown): boolean =>
  (typeof value === 'object' && value !== null) || typeof value === 'function';

/** A property, or undefined where there is nothing there and where the read throws. */
export const readProperty = (target: unknown, key: string): unknown => {
  if (!isObjectLike(target)) return undefined;
  try {
    return (target as Record<string, unknown>)[key];
  } catch {
    return undefined;
  }
};

/** A non empty string, or null. Used wherever a name or a message is expected. */
export const readString = (target: unknown, key: string): string | null => {
  const value = readProperty(target, key);
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
};

/**
 * The name and message of something thrown.
 *
 * The name matters on its own. Requirement 1.8 asks the capability panel to state
 * `NotAllowedError` as the cause when registration inside the partner frame is
 * refused, so a rejection reduced to a boolean, or to a message alone, loses the
 * one fact the panel needs.
 *
 * `apps/jig/src/platform/reads.ts` holds a helper of the same name doing the same
 * job. This is a deliberate duplication of nine lines rather than a shared import,
 * because a package may not import an app, and moving the app's copy into a shared
 * package would edit `apps/jig`, which task 3.7 does not own. Whichever task next
 * touches both is welcome to collapse them.
 */
export interface ThrownDetail {
  readonly name: string;
  readonly message: string;
}

export const describeThrown = (thrown: unknown): ThrownDetail => {
  const name = readString(thrown, 'name');
  const message = readString(thrown, 'message');
  if (name !== null || message !== null) {
    return { name: name ?? 'Error', message: message ?? '' };
  }
  return { name: 'Error', message: typeof thrown === 'string' ? thrown : String(thrown) };
};

/**
 * The slice of `document.modelContext` these two registration paths use.
 *
 * Both methods are optional because both are absent on some builds, and the
 * absence of `registerTool` is a supported state rather than an error: the
 * workbench runs with no tools at all, per requirement 20.1.
 *
 * The call shape is `registerTool(descriptor, options)`, and the options object is
 * where the `AbortSignal` goes. That is a measurement rather than a guess: on Chrome
 * Canary 154.0.8032.0 with `--enable-features=WebMCP`, a signal handed over as a
 * field on the descriptor is ignored, and the same signal handed over as the second
 * argument withdraws the tool when it is aborted. One version on one machine, so the
 * descriptor field is still sent as well, but the second argument is the route that
 * was seen to work.
 *
 * `unregisterTool` is typed as optional because the guarded fallbacks in this
 * package and its callers still call it where a build offers it. **No build we have
 * measured offers it.** The whole surface on that Chrome build is `ontoolchange`,
 * `executeTool`, `getTools` and `registerTool`, so every `unregisterTool` path is
 * dead code there and nothing may be built on the assumption that it runs.
 */
export interface ModelContextLike {
  readonly registerTool?: ((descriptor: unknown, options?: unknown) => unknown) | undefined;
  readonly unregisterTool?: ((name: string) => unknown) | undefined;
}

/** A method off the host, or null where the property is not callable. */
export type HostMethod = (...args: ReadonlyArray<unknown>) => unknown;

export const readMethod = (target: unknown, key: string): HostMethod | null => {
  const value = readProperty(target, key);
  return typeof value === 'function' ? (value as HostMethod) : null;
};

/**
 * The typed door for a caller holding an unknown.
 *
 * The platform layer reads `document.modelContext` as `unknown`, by design, so it
 * needs one place to narrow that value before handing it over.
 */
export const asModelContext = (value: unknown): ModelContextLike | null =>
  isObjectLike(value) ? (value as ModelContextLike) : null;
